import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["errorMessage", "paymentElement", "submitBtn", "paymentMethodField", "paymentToken", "klarnaInput"]
  static values = {
    stripePublicKey: String,
    locale: String,
    errorMessage: String,
    currency: { type: String, default: "eur" },
    amount: Number,
    onBehalfOf: String,
    app: { type: Boolean, default: false },
    domain: String,
    disabledPaymentMethods: Array,
    customerId: String,
    autoSaveCards: { type: Boolean, default: false },
  }

  connect() {
    // Initialize bypass flag for Stripe processing
    this.bypassStripeProcessing = false

    this.stripe = window.Stripe(this.stripePublicKeyValue)

    // Find the closest form element
    this.form = this.element.closest("form")

    // Disable Rails UJS on submit button to prevent conflicts
    this.submitBtnTarget.removeAttribute("data-disable-with")

    // Set up Elements with manual payment method creation
    this.setupElements()
  }

  disconnect() {
    // Clean up event listeners
    if (this.paymentElement) {
      this.paymentElement.off("change", this.handlePaymentMethodChange)
    }
  }

  async setupElements() {
    var elementsConfig = {
      mode: "payment",
      currency: this.currencyValue,
      amount: this.amountValue,
      paymentMethodCreation: "manual",
      locale: this.localeValue,
      captureMethod: "manual"
    }

    if (this.autoSaveCardsValue) {
      // eslint-disable-next-line camelcase
      elementsConfig.setup_future_usage = "off_session"
    }

    if (this.onBehalfOfValue !== "") {
      // eslint-disable-next-line camelcase
      elementsConfig.on_behalf_of = this.onBehalfOfValue
    }

    if (this.hasDisabledPaymentMethodsValue && this.disabledPaymentMethodsValue.length > 0) {
      elementsConfig.excludedPaymentMethodTypes = this.disabledPaymentMethodsValue
    }

    // Always try to fetch customer session (creates customer if needed)
    try {
      const customerSession = await this.fetchCustomerSession()
      if (customerSession && customerSession.client_secret) {
        elementsConfig.customerSessionClientSecret = customerSession.client_secret
      }
    } catch (error) {
      console.error("Customer session creation failed:", error)
    }
    this.elements = this.stripe.elements(elementsConfig)

    const paymentElementOptions = {
      paymentMethodOrder: [
        "card",
        "google_pay",
        "apple_pay",
        "klarna",
      ]
    }

    if (this.autoSaveCardsValue) {
      paymentElementOptions.wallets = {
        applePay: "never",
        googlePay: "never",
        link: "never",
      }
    }

    this.paymentElement = this.elements.create("payment", paymentElementOptions)
    this.paymentElement.mount(this.paymentElementTarget)

    // Store the original button text
    this.originalButtonText = this.submitBtnTarget.value

    // Bind the handler to maintain the correct 'this' context
    this.handlePaymentMethodChange = this.handlePaymentMethodChange.bind(this)

    // Listen for payment method changes
    this.paymentElement.on("change", this.handlePaymentMethodChange)
  }

  handlePaymentMethodChange(event) {
    if (event.value && event.value.type === "klarna") {
      this.submitBtnTarget.value = this.klarnaInputTarget.value
    } else {
      this.submitBtnTarget.value = this.originalButtonText
    }
  }

  async handleSubmit(e) {
    // If we're bypassing Stripe processing (final submission), allow normal form submission
    if (this.bypassStripeProcessing) {
      return
    }

    // Check if payment method is cash - if so, allow normal form submission
    if (this.hasPaymentMethodFieldTarget && this.paymentMethodFieldTarget.value === "cash") {
      // Allow normal form submission for cash payments
      return
    }

    // Only prevent default and handle Stripe payment if method is card
    e.preventDefault()
    e.stopPropagation()

    // Prevent multiple form submissions
    if (this.submitBtnTarget.disabled) {
      return
    }

    // Disable form submission while loading
    this.submitBtnTarget.disabled = true

    // Clear any previous error messages
    this.errorMessageTarget.textContent = ""

    try {
      // Step 1: Trigger form validation and wallet collection
      const { error: submitError } = await this.elements.submit()
      if (submitError) return this.handleError(submitError)

      // Step 2: Create the ConfirmationToken using the details collected by the Payment Element
      const { error: tokenError, confirmationToken } =
        await this.stripe.createConfirmationToken({
          elements: this.elements,
          params: {
            // eslint-disable-next-line camelcase
            return_url: this.getReturnUrl(),
            // eslint-disable-next-line camelcase
            payment_method_data: this.autoSaveCardsValue === true ? {
              // eslint-disable-next-line camelcase
              allow_redisplay: "always"
            } : undefined
          },
        })

      if (tokenError || !confirmationToken) {
        // This point is only reached if there's an immediate error when
        // creating the confirmationToken. Show the error to your customer
        this.handleError(tokenError || { message: "No token received" })
        return
      }

      // Step 3: Send confirmation token to backend and handle the response
      await this.fetchAndProcessPayment(confirmationToken)
    } catch (err) {
      console.error("Payment processing error:", err)
      this.handleError({ message: this.errorMessageValue })
    }
  }

  handleError(error) {
    this.errorMessageTarget.textContent = error.message
    this.submitBtnTarget.disabled = false
    window.message_error(error.message)
  }

  async fetchAndProcessPayment(confirmationToken) {
    try {
      // Send confirmation token to backend to create PaymentIntent
      const {
        clientSecret,
        paymentIntentStatus,
        error: backendError,
      } = await this.createPaymentIntentWithToken(confirmationToken.id)

      if (backendError) return this.handleError({ message: backendError })

      // Check if we have a valid payment intent status
      if (!paymentIntentStatus) {
        this.handleError({ message: "Invalid payment intent status" })
        return
      }

      // Only handle next action if the PaymentIntent requires it
      if (paymentIntentStatus === "requires_action") {
        const { error: nextActionError } = await this.stripe.handleNextAction({
          clientSecret: clientSecret,
        })

        if (nextActionError) return this.handleError(nextActionError)
      }

      // Submit the form to complete the booking using Rails UJS with regular form submission fallback
      this.bypassStripeProcessing = true
      if (this.form.dataset.remote) {
        Rails.fire(this.form, "submit")
      } else {
        this.form.submit()
      }
    } catch (error) {
      console.error("Payment processing failed:", error)
      this.handleError({ message: this.errorMessageValue || "Payment processing failed" })
    }
  }

  async createPaymentIntentWithToken(confirmationTokenId) {
    return new Promise((resolve) => {
      // Use current URL with all parameters and just change the path to payment_intents
      const paymentIntentUrl = this.getPaymentIntentUrl(confirmationTokenId)

      Rails.ajax({
        type: "POST",
        url: paymentIntentUrl,
        success: (data) => {
          // Update the hidden field with the payment intent ID
          const paymentIntentField = this.form.querySelector("#payment_intent_id")
          if (paymentIntentField) {
            paymentIntentField.value = data.payment_intent_id
          }

          resolve({
            clientSecret: data.client_secret,
            paymentIntentStatus: data.payment_intent_status,
          })
        },
        error: (xhr) => {
          let errorMessage = "Payment processing failed"

          // Check if xhr is already the error object (Rails UJS preprocessing)
          if (xhr && typeof xhr === "object" && xhr.error) {
            errorMessage = xhr.error
          } else if (xhr && xhr.responseJSON && xhr.responseJSON.error) {
            errorMessage = xhr.responseJSON.error
          }

          resolve({ error: errorMessage })
        }
      })
    })
  }

  getReturnUrl() {
    // Create return URL for redirects (Klarna, etc.)
    const currentUrl = new URL(window.location.href)

    // Preserve all array parameters (those ending with [])
    // URLSearchParams can lose duplicate parameters when manipulating the URL,
    // so we need to store them before making any changes.
    // We'll convert them to indexed format (key[0], key[1]) to match Klarna/Stripe expectations.
    // Common array parameters include: service_options[], barbers[], filter_barbers[]
    const arrayParams = {}

    // Find all parameters that end with [] (array parameters)
    for (const [key, value] of currentUrl.searchParams.entries()) {
      if (key.endsWith("[]")) {
        if (!arrayParams[key]) {
          arrayParams[key] = []
        }
        arrayParams[key].push(value)
      }
    }

    // Clean up Stripe-related parameters that shouldn't be in the return URL
    currentUrl.searchParams.delete("payment_intent")
    currentUrl.searchParams.delete("payment_intent_client_secret")
    currentUrl.searchParams.delete("redirect_status")
    currentUrl.searchParams.delete("open_in_app")

    // Re-add all array parameters as indexed hash format (key[0], key[1], etc.)
    // This matches the format that Klarna/Stripe expects and prevents deduplication
    Object.keys(arrayParams).forEach(param => {
      currentUrl.searchParams.delete(param) // Clear any existing values first
      const baseParam = param.replace("[]", "") // Remove [] to get base parameter name
      arrayParams[param].forEach((value, index) => {
        currentUrl.searchParams.append(`${baseParam}[${index}]`, value)
      })
    })

    // Add open_in_app parameter if we're in app mode
    if (this.appValue && this.domainValue) {
      currentUrl.searchParams.set("open_in_app", "true")
      currentUrl.hostname = this.domainValue
    }

    return currentUrl.toString()
  }


  getPaymentIntentUrl(confirmationTokenId) {
    // Create URL with all current parameters and change path to payment_intents
    const currentUrl = new URL(window.location.href)

    // Replace /confirm/new with /payment_intents for bookings
    // Replace /payments/new with /payments/payment_intents for products
    let newPath
    if (currentUrl.pathname.includes("/confirm/new")) {
      newPath = currentUrl.pathname.replace("/confirm/new", "/payment_intents")
    } else if (currentUrl.pathname.includes("/payments/new")) {
      newPath = currentUrl.pathname.replace("/payments/new", "/payments/payment_intents")
    } else if (currentUrl.pathname.match(/\/payments\/[^\/]+\/(edit|prompt)$/)) {
      // Handle /payments/{token}/edit or /payments/{token}/prompt -> /payments/payment_intents
      newPath = "/payments/payment_intents"
    } else if (currentUrl.pathname.match(/\/appointments\/[^\/]+\/edit$/)) {
      // Handle /appointments/{token}/edit -> /payments/payment_intents
      newPath = "/payments/payment_intents"
    } else {
      // Fallback: append /payment_intents to current path
      newPath = currentUrl.pathname.replace(/\/new$/, "/payment_intents")
    }

    currentUrl.pathname = newPath

    // Add confirmation_token to the query parameters
    currentUrl.searchParams.set("confirmation_token", confirmationTokenId)

    // Add payment_token if available
    if (this.hasPaymentTokenTarget && this.paymentTokenTarget.value) {
      currentUrl.searchParams.set("payment_token", this.paymentTokenTarget.value)
    }

    return currentUrl.toString()
  }

  async fetchCustomerSession() {
    return new Promise((resolve, reject) => {
      Rails.ajax({
        type: "POST",
        url: "/payments/stripe_customer_sessions",
        success: (data) => {
          resolve(data)
        },
        error: (xhr) => {
          console.error("Customer session creation failed:", xhr)
          if (xhr.responseJSON && xhr.responseJSON.error) {
            reject(new Error(xhr.responseJSON.error))
          } else {
            reject(new Error("Customer session creation failed"))
          }
        }
      })
    })
  }
}
